import { useState, useEffect } from 'react';
import { createClient, Session, User, AuthChangeEvent, SupabaseClient } from '@supabase/supabase-js';
import { usePostSignupActions } from './usePostSignupActions'; // Import the hook
import { rememberAuthReturn, safeInternalPath } from '../utils/conversionJourney';
import { trackGa4 } from '../utils/analytics';

// Ensure environment variables are defined
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl) {
  throw new Error("Missing environment variable: REACT_APP_SUPABASE_URL");
}
if (!supabaseAnonKey) {
  throw new Error("Missing environment variable: REACT_APP_SUPABASE_ANON_KEY");
}

// Initialize Supabase client
const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey);

// Define the signUp options interface for clarity
interface SignUpOptions {
  data?: {
    full_name?: string;
    [key: string]: any; // Allow other metadata properties 
  };
  [key: string]: any; // Allow other options like redirectTo
}

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const { handlePostSignupActions } = usePostSignupActions();

  useEffect(() => {
    // Check initial session state
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error("Error getting initial session:", error);
        setError(error);
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for authentication state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event: AuthChangeEvent, session: Session | null) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        setError(null);

        if (event === "SIGNED_IN") {
          // This is the first time we see the user, check for welcome bonus
          if (session?.user) {
            // Email sign-ups are counted on the sign-up form; OAuth accounts are
            // counted here when they were created in the last few minutes.
            const provider = session.user.app_metadata?.provider;
            const createdAt = Date.parse(session.user.created_at || "");
            if (provider && provider !== "email" && Date.now() - createdAt < 10 * 60 * 1000) {
              trackGa4("sign_up", { method: String(provider) }, { persist: `sign_up:${session.user.id}` });
            }
            console.log("User signed in, checking for post-signup actions...");
            const result = await handlePostSignupActions(session.user.id);
            if (result.success && result.granted_credits) {
              // Use a global notification system if available
              (window as any).showNotification?.(
                `🎉 Welcome! You received ${result.granted_credits} bonus credits!`,
                "success"
              );
            }
            // Check for pending supervisor invitations (fire-and-forget)
            try {
              const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
              fetch(`${apiUrl}/api/auth/check-pending-invitations`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  ...(session?.access_token
                    ? { Authorization: `Bearer ${session.access_token}` }
                    : {}),
                },
                body: JSON.stringify({
                  user_id: session.user.id,
                  user_email: (session.user as any)?.email,
                }),
              }).then(async (res) => {
                if (res.ok) {
                  const data = await res.json();
                  if (data.results && data.results.length > 0) {
                    (window as any).showNotification?.(
                      "You've been linked to a supervisor and granted Premium Plus access!",
                      "success"
                    );
                  }
                }
              }).catch(() => {});
            } catch {}
          }
        } else if (event === "PASSWORD_RECOVERY") {
          window.location.replace("/reset-password");
        }
      }
    );

    // Cleanup listener on component unmount
    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []); // handlePostSignupActions is stable, so no need to add to dependency array

  // Authentication methods
  const signInWithPassword = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.error("Error signing in:", error);
        setError(error);
        return { user: null, error };
      }
      
      return { user: data.user, error: null };
    } catch (err) {
      const error = err as Error;
      console.error("Exception during sign in:", error);
      setError(error);
      return { user: null, error }; // Ensure consistent return shape
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, options?: SignUpOptions) => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options
      });
      
      if (error) {
        console.error("Error signing up:", error);
        setError(error);
        return { user: null, error };
      }
      
      return { user: data.user, error: null };
    } catch (err) {
      const error = err as Error;
      console.error("Exception during sign up:", error);
      setError(error);
      return { user: null, error };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("Error signing out:", error);
        setError(error);
      }
    } catch (err) {
      const error = err as Error;
      console.error("Exception during sign out:", error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const sendPasswordResetEmail = async (email: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      
      if (error) {
        console.error("Error sending password reset:", error);
        setError(error);
        return { error };
      }
      
      return { error: null };
    } catch (err) {
      const error = err as Error;
      console.error("Exception during password reset:", error);
      setError(error);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async (
    _intent: 'login' | 'signup' = 'login',
    returnTo?: string | null
  ) => {
    setLoading(true);
    setError(null);
    try {
      try {
        const safeReturn = safeInternalPath(returnTo);
        if (safeReturn) rememberAuthReturn(safeReturn);
      } catch { }
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/login?auth_callback=1`,
        },
      });
      if (error) {
        console.error("Google sign-in error:", error);
        setError(error);
      }
    } catch (err) {
      const error = err as Error;
      console.error("Exception during Google sign-in:", error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (data: { full_name?: string }) => {
    setLoading(true);
    setError(null);
    try {
      const { data: result, error } = await supabase.auth.updateUser({
        data: data
      });
      if (error) {
        setError(error);
        return { error };
      }
      if (result.user) {
        setUser(result.user);
      }
      return { error: null };
    } catch (err) {
      const error = err as Error;
      setError(error);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  return {
    session,
    user,
    loading,
    error,
    signInWithPassword,
    signUp,
    signOut,
    sendPasswordResetEmail,
    signInWithGoogle,
    updateProfile,
    supabase
  };
}
